/* eslint-disable no-nested-ternary */
import { useState, ChangeEvent, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelectedContactStore } from 'store'
import { accentFold } from 'utils'
import { getExternalAccountContacts } from 'api/bank'
import { trackEvent } from 'analytics/trackFunctions'

// Components
import Skeleton from 'react-loading-skeleton'
import ContactCard from 'components/ContactCard'
import PageWrapper from 'components/PageWrapper'
import SearchInput from 'components/SearchInput'
import PlusSign from 'assets/icons/PlusSign'
import EmptySection from 'components/EmptySection'

const Contacts = () => {
  const [searchInputValue, setSearchInputValue] = useState('')
  const [contacts, setContacts] = useState([])
  const [filteredContacts, setFilteredContacts] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const { updateSelectedContact } = useSelectedContactStore()

  const navigate = useNavigate()

  useEffect(() => {
    getExternalAccountContacts()
      .then((res) => {
        if (res.success) {
          const { result } = res.data

          const arr = Array.from(Object.entries(result), ([key, val]) => ({
            accountHolder: key,
            accounts: val,
            taxID: val[0].tax_identification_value,
            // case-folding and accent-folding for search
            normalizedAccountHolder: accentFold(key).toLowerCase()
          }))

          setContacts(arr)
          setFilteredContacts(arr)
        }
      })
      .finally(() => setIsLoading(false))
  }, [])

  const handleContactButton = (taxID) => {
    updateSelectedContact(taxID)
    navigate('/transferencias/contacto')
  }

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    const searchTerm = e.target.value
    setSearchInputValue(searchTerm)

    const normalizedInput = accentFold(searchTerm).toLowerCase()

    const filteredItems = contacts.filter((c) =>
      c.normalizedAccountHolder.includes(normalizedInput)
    )
    setFilteredContacts(filteredItems)
  }

  const handleCleanInput = () => {
    setSearchInputValue('')
    setFilteredContacts(contacts)
  }

  return (
    <PageWrapper noTabBar title='Transferir dinero' viewSelected='/movimientos'>
      <section className='flex flex-col text-black mx-auto justify-center sm:max-w-[22.5rem]'>
        <h2 className='text-xs font-medium text-left self-start'>
          Elegí una opción para transferir
        </h2>
        <NewAccountButton className='mt-3' />
        <h2 className='text-xs font-medium text-left self-start my-5'>
          Mis contactos
        </h2>
        {!isLoading && contacts.length > 0 && (
          <SearchInput
            value={searchInputValue}
            handler={(e) => handleSearch(e)}
            setValue={setSearchInputValue}
            handleCleanInput={handleCleanInput}
            placeholder='Buscar...'
          />
        )}
        {!isLoading && contacts.length > 0 ? (
          <div className='my-4'>
            {filteredContacts.map((c, idx) => (
              <ContactCard
                key={c.taxID || idx}
                name={c.accountHolder}
                bankImages={c.accounts.map((acc) => acc.bank_image)}
                className='mb-3'
                onClick={() => handleContactButton(c.taxID)}
              />
            ))}
          </div>
        ) : isLoading ? (
          <Skeleton count={2} className='w-full my-2' height={69} />
        ) : (
          <EmptySection className='mt-0' section='CONTACTS' />
        )}
      </section>
    </PageWrapper>
  )
}

export default Contacts

const NewAccountButton = ({ className = '' }) => {
  const navigate = useNavigate()
  return (
    <button
      type='button'
      onClick={() => {
        trackEvent({ eventName: 'bille_boton_nueva_cuenta' })
        navigate('/transferencias/nueva-cuenta')
      }}
      className={`bg-white rounded-lg p-3 w-full flex gap-2 items-center z-1 relative border border-gray-500 ${className}`}
    >
      <PlusSign className='m-[10px]' />
      <span className='font-bold text-xs'>
        Enviar dinero a una nueva cuenta
      </span>
    </button>
  )
}
